/**
 * Master defaults for the MyHub (ZiniHub) frontend.
 *
 * This module should contain only serializable constants (no functions on exported config).
 * Add more feature blocks alongside SUBSCRIPTION_CONFIG (e.g. ORDERS_CONFIG) as the app grows.
 *
 * Future: load overrides from DB/S3 and merge into these defaults. Only the merge/bootstrap
 * logic should live here; until then this file is the single source of default constants.
 *
 * @example
 * // mergeZinihubDefaultsWithRemote(remotePayload) — placeholder name for future use
 */

const SUBSCRIPTION_LABELS = {
  PRICING: {
    url: "https://ziniz.online/ZiniHub/#pricing",
    linkLabel: "Click Here",
    planDetailsLabel: "Compare plan benefits ",
    supportUrl: "https://ziniz.online/ContactSupport/",
    supportLabel: "Contact support",
  },
  PAGE: {
    title: "Subscription",
    subtitle: "View and manage your subscription.",
    hubPlaceholder: "Select Hub",
  },
  LOADING: {
    text: "Loading...",
  },
  CATALOG: {
    unavailable: "Subscription plans are temporarily unavailable.",
  },
  EMPTY: {
    noHub:
      "No hub assigned yet. Complete hub setup to see subscription details.",
    selectHub: "Select a hub to view subscription.",
    noSubscriptionData: "No subscription data found for this hub.",
  },
  CARDS: {
    currentPlan: "Current plan",
    lastAccessed: "Last accessed",
    startDate: "Start date",
    expiryDate: "Expiry date",
    planValueFallback: "—",
  },
  EXPIRY: {
    expired: "Expired",
  },
  ACTIONS: {
    cardTitle: "Subscription actions",
    renew: "Renew",
    upgradePlan: "Upgrade plan",
    cancelSubscription: "Cancel subscription",
    downgrade: "Downgrade",
    compareBenefitsBeforeLink: "To compare plan benefits please ",
    compareBenefitsAfterLink: ".",
  },
  CANCEL: {
    popconfirmTitle: "Cancel subscription?",
    popconfirmDescription:
      "Your subscription will be cancelled. You will lose access at the end of the current billing period.",
    okText: "Yes, cancel",
    cancelText: "No",
  },
  DOWNGRADE: {
    disabledTitle: "Downgrade is not available for Trial or Starter plans",
    selectPlanPlaceholder: "Select plan",
    popconfirmTitle: "Downgrade subscription?",
    beforePlanName: "Your plan will change to ",
    afterPlanName: ". Some features may be limited.",
    okText: "Yes, downgrade",
    cancelText: "No",
  },
  RENEW_MODAL: {
    title: "Renew subscription",
    popconfirmTitle: "Renew subscription?",
    proceedToPay: "Proceed to Pay",
    cancel: "Cancel",
    continue: "Continue",
    renew: "Renew",
    trialHint: "Trial cannot be renewed. Select a plan to renew:",
    trialDescriptionBeforePlan: "Proceed to payment for ",
    trialDescriptionAfterValidity: "?",
    paidDescriptionBeforePlan: "Renew your current ",
    paidDescriptionMiddle: " plan (",
    paidDescriptionAfterValidity: "?",
  },
  UPGRADE_MODAL: {
    title: "Upgrade plan",
    popconfirmTitle: "Upgrade subscription?",
    upgradeButton: "Upgrade",
    selectPlanPrompt: "Select the plan you want to upgrade to:",
    alertMessage: "Upgrade timing",
    /** Placeholder `{finalDays}` is replaced in SubscriptionHelpers using UPGRADE_OPTIONS_FINAL_DAYS. */
    alertDescription:
      "Plan upgrades are available during the final {finalDays} days before your subscription expires. Please check back when you are within that window, or use Renew if you need to extend your current plan.",
    chargedPrefix: "You will be charged for upgraded ",
    chargedMiddle: " plan (",
    chargedSuffix: ")",
  },
  DOWNGRADE_MODAL: {
    title: "Downgrade plan",
    selectPlanPrompt: "Select the plan you want to downgrade to:",
    downgradeButton: "Downgrade",
  },
  FIELDS: {
    plan: "Plan",
    validity: "Validity",
  },
  TOASTS: {
    missingPaymentSession: "Missing payment session. Cannot verify payment.",
    invalidPaymentVerificationResponse:
      "Invalid payment verification response from server.",
    paymentCouldNotVerify: "Payment could not be verified.",
    paymentVerifiedUpdated: "Payment verified. Subscription has been updated.",
    paymentVerifiedYourUpdated:
      "Payment verified. Your subscription has been updated.",
    paymentVerifyTryAgain:
      "Payment could not be verified. Please try again or contact support.",
    paymentStillConfirming:
      "Payment is still being confirmed. Please check back shortly.",
    couldNotConfirmPayment:
      "Could not confirm payment. Please try again or contact support.",
    noHubProfile: "No hub profile selected.",
    notSignedIn: "You are not signed in.",
    paymentCheckoutFailed: "Payment checkout failed.",
    requestFailed: "Request failed.",
    requestCompleted: "Request completed successfully.",
    requestTryAgain: "Request failed. Please try again.",
    selectPlan: "Please select a plan.",
    selectPlanDowngrade: "Please select a plan to downgrade to.",
  },
};

/**
 * Subscription feature defaults. LABELS holds all user-visible strings; scalars are top-level.
 */
export const SUBSCRIPTION_CONFIG = {
  LABELS: SUBSCRIPTION_LABELS,
  /** Paid plans show upgrade UI when remainingDays <= this (trial always sees upgrade UI). */
  UPGRADE_OPTIONS_FINAL_DAYS: 7,
};

/**
 * Limits for product/service variant and customisation editors (ItemForm).
 */
export const ITEM_PRICING_CONFIG = {
  maxVariants: 5,
  maxOptionsPerVariant: 20,
  maxCustomisations: 10,
};

/**
 * User-visible strings for login and password-reset screens.
 * Import `AUTH` (or `ZINIHUB.AUTH`, same object), e.g. `AUTH.LOGIN_TITLE`.
 */
export const AUTH = {
  LOGIN_TITLE: "Login to ZiniHub Admin",
  LOGIN_PHONE_LABEL: "* Mobile (WhatsApp number)",
  LOGIN_PHONE_PLACEHOLDER: "Mobile number",
  LOGIN_PHONE_REQUIRED: "Please enter your mobile number",
  LOGIN_PHONE_INVALID: "Enter valid 10-digit number",
  LOGIN_PASSWORD_LABEL: "* Password",
  LOGIN_PASSWORD_REQUIRED: "Please enter your Password",
  LOGIN_SUBMIT: "LOGIN",
  LOGIN_SUBMITTING: "Validating...",
  LOGIN_SUCCESS: "Logged in successfully.",
  LOGIN_ERROR_DEFAULT: "Login failed. Please check your credentials.",
  LOGIN_ERROR_INVALID_CREDENTIALS: "Invalid mobile or password",
  LOGIN_ERROR_UNKNOWN: "Something is not right",
  LOGIN_PHONE_INVALID_LENGTH: "Enter 6\u201314 digits",
  LOGIN_PASSWORD_LABEL_PLAIN: "Password",
  LOGIN_LINK_NEW_USER: "New user? ",
  LOGIN_LINK_CREATE_ACCOUNT: "Create account",
  LOGIN_LINK_RESET: "Forgot Password?",
  REGISTER_TITLE: "Register as a Merchant",
  REGISTER_STEP1_LABEL: "Step 1 of 3 \u2014 Verify your number",
  REGISTER_STEP1_DESC: "We'll send a WhatsApp OTP to confirm.",
  REGISTER_MOBILE_LABEL: "* Mobile (WhatsApp only)",
  REGISTER_MOBILE_REQUIRED: "Enter your mobile number",
  REGISTER_MOBILE_INVALID: "Enter 6\u201314 digits",
  REGISTER_MOBILE_DIGITS: "Digits only",
  REGISTER_OTP_HEADER: "OTP sent to WhatsApp",
  REGISTER_OTP_RESEND: "Resend OTP",
  REGISTER_OTP_CHANGE: "Change number",
  REGISTER_VERIFY: "Verify",
  REGISTER_VERIFY_SENDING: "Sending\u2026",
  REGISTER_EDIT: "Edit",
  REGISTER_CONTINUE: "Continue \u2192",
  REGISTER_LOGIN_LINK: "Already have an account? ",
  REGISTER_LOGIN_CTA: "Login",
  REGISTER_STEP2_LABEL: "Step 2 of 3 \u2014 Create your account",
  REGISTER_STEP2_DESC: "Just the essentials.",
  REGISTER_FIRST_NAME: "First name",
  REGISTER_FIRST_NAME_REQUIRED: "Enter your first name",
  REGISTER_LAST_NAME: "Last name",
  REGISTER_LAST_NAME_REQUIRED: "Enter your last name",
  REGISTER_PASSWORD_HINT: "Min 5 characters",
  REGISTER_PASSWORD_LABEL: "Password",
  REGISTER_PASSWORD_REQUIRED: "Enter a password",
  REGISTER_PASSWORD_MINLENGTH: "5 characters or more",
  REGISTER_CONFIRM_PASSWORD: "Confirm password",
  REGISTER_CONFIRM_PASSWORD_REQUIRED: "Confirm your password",
  REGISTER_PASSWORDS_MISMATCH: "Passwords do not match!",
  REGISTER_EMAIL_INVALID: "Enter a valid email",
  REGISTER_EMAIL_REQUIRED: "Enter your email",
  REGISTER_VERIFY_FAILED: "Verification failed. Try again.",
  REGISTER_ERROR_UNKNOWN: "Something is not right",
  REGISTER_BACK: "\u2190 Back",
  REGISTER_STEP3_LABEL: "Step 3 of 3 \u2014 A few more details",
  REGISTER_STEP3_AMBER: "Email is required. Date of birth is optional.",
  REGISTER_USERNAME_LABEL: "Username (Auto-assigned)",
  REGISTER_USERNAME_REQUIRED: "Verify mobile first",
  REGISTER_USERNAME_HINT: "Read-only. Can change in settings.",
  REGISTER_EMAIL_LABEL: "Email",
  REGISTER_EMAIL_PLACEHOLDER: "Enter email",
  REGISTER_EMAIL_HINT: "Verify button appears after entry",
  REGISTER_EMAIL_OTP_HEADER: "OTP sent to email",
  REGISTER_EMAIL_CHANGE: "Change email",
  REGISTER_DOB_LABEL: "Date of birth (optional)",
  REGISTER_DOB_PLACEHOLDER: "e.g. 1995-Jan-12",
  REGISTER_SUBMIT: "Register \u2192",
  REGISTER_SUBMITTING: "Creating Account...",
  RESET_PAGE_TITLE: "Password Reset",
  RESET_STEP1_LABEL: "Step 1 of 2 \u2014 Verify your number",
  RESET_STEP1_DESC: "Enter your registered mobile to receive an OTP.",
  RESET_MOBILE_LABEL: "* Registered Mobile",
  RESET_MOBILE_REQUIRED: "Enter your registered mobile",
  RESET_OTP_HEADER: "OTP sent to WhatsApp",
  RESET_OTP_RESEND: "Resend OTP",
  RESET_OTP_CHANGE: "Change number",
  RESET_CONTINUE: "Continue \u2192",
  RESET_LOGIN_LINK: "Remember your password? ",
  RESET_LOGIN_CTA: "Login",
  RESET_STEP2_LABEL: "Step 2 of 2 \u2014 Change password",
  RESET_STEP2_DESC: "Choose a new password for your account.",
  RESET_USERNAME_LABEL: "User Name (Auto-populated)",
  RESET_USERNAME_REQUIRED: "User name is required",
  RESET_NEW_PASSWORD: "New password",
  RESET_CONFIRM_PASSWORD: "Confirm password",
  RESET_CANCEL: "Cancel",
  RESET_BACK: "\u2190 Back",
  RESET_PASSWORD_REQUIRED: "Enter a password",
  RESET_PASSWORD_MINLENGTH: "5 characters or more",
  RESET_CONFIRM_PASSWORD_REQUIRED: "Confirm your password",
  RESET_PASSWORDS_MISMATCH: "Passwords do not match!",
  RESET_ERROR_DEFAULT: "Unexpected error. Try again later.",
  RESET_ERROR_FAILED: "Password change failed. Try again.",
  RESET_ERROR_UNKNOWN: "Something is not right",
  RESET_MOBILE_PLACEHOLDER: "Mobile number",
  RESET_SUBMIT: "Reset Password",
  RESET_SUBMITTING: "Changing Password...",
};

/**
 * Footer text for all shells (admin, customer, auth pages).
 */
const isProd = (process.env.REACT_APP_DEPLOY_ENV || "").trim() === "Prod";
const CUSTOMER_DOMAIN = isProd ? "zinihub.com" : "visitmyhub.online";
export const FOOTER = {
  ADMIN: "ZiniHub©2026 Powered by",
  CUSTOMER: CUSTOMER_DOMAIN,
  BRAND_LINK: "ziniz",
};

/**
 * Labels for PublicShell (auth pages).
 */
export const PUBLIC_SHELL = {
  BRAND: {
    LOGO_LETTER: "Z",
    NAME: "ZiniHub",
    TAGLINE: "Your businesses, one hub.",
    TAGLINE_DESKTOP_1: "Your businesses,",
    TAGLINE_DESKTOP_2: "one hub.",
    DESCRIPTION: "Manage stores, track orders, connect with customers — all in one place.",
  },
  VALUE_PROPS: [
    { label: "Multi-store management", description: "Manage all your businesses from one dashboard." },
    { label: "Unified order tracking", description: "Track every order and booking from confirmation to completion." },
    { label: "WhatsApp-native", description: "Connect with customers through familiar WhatsApp workflows." },
  ],
  TRUST_BLOCK: {
    TITLE: "Powered by Ziniz",
    DESCRIPTION_PREFIX: "Secure, fast, and trusted by over ",
    HIGHLIGHT: "100+ local businesses",
    DESCRIPTION_SUFFIX: " serving their communities better.",
  },
};

/**
 * Labels for AdminAppShell (admin dashboard).
 */
export const ADMIN_SHELL = {
  BRAND: {
    LOGO_LETTER: "Z",
    NAME: "ZiniHub",
  },
  NAV: [
    { key: "/dashboard",     label: "Dashboard" },
    { key: "/hub",           label: "My Hub" },
    { key: "/gallery",       label: "Media Gallery" },
    { key: "/hubSettings",   label: "Hub Settings" },
    { key: "/subscription",  label: "Subscription" },
    { key: "/staff",         label: "Staff Management",    comingSoon: true },
    { key: "/notebook",      label: "Pocket Diary" },
    { key: "/log",           label: "Log",            comingSoon: true },
  ],
  LABELS: {
    LOGOUT: "Logout",
    BREADCRUMB_USER_FALLBACK: "User",
    BREADCRUMB_PAGE_FALLBACK: "Page",
    COLLAPSE_SIDEBAR: "Collapse sidebar",
    EXPAND_SIDEBAR: "Expand sidebar",
    OPEN_NAVIGATION: "Open navigation",
    CLOSE_NAVIGATION: "Close navigation",
  },
};

/**
 * User-visible strings for the Hub Setup first-run modal.
 */
export const HUB_SETUP = {
  TITLE: "Let's setup your Hub",
  INFO_TEXT:
    "A Hub is a centralised place for all your businesses. Customers land here to access all your listed stores.",
  DISPLAY_NAME_LABEL: "Hub Display Name",
  DISPLAY_NAME_PLACEHOLDER: "e.g. My Business Hub",
  DISPLAY_NAME_REQUIRED: "Please enter a display name for your hub",
  DISPLAY_NAME_MIN: "Display name must be at least 3 characters",
  DISPLAY_NAME_MAX: "Display name must not exceed 50 characters",
  DOMAIN_LABEL: "* Domain Name",
  DOMAIN_PLACEHOLDER: "e.g. mybusiness",
  DOMAIN_REQUIRED: "Please enter a domain name for your hub",
  DOMAIN_PATTERN:
    "Domain Name can only contain lowercase letters, numbers, and hyphens",
  DOMAIN_MIN: "Domain Name must be at least 3 characters",
  DOMAIN_MAX: "Domain Name must not exceed 30 characters",
  DOMAIN_URL_PREVIEW: `yourdomain.${CUSTOMER_DOMAIN}`,
  CHECK_BTN: "\u2713 Check",
  CHECK_DOMAIN_FIRST: "Please check domain availability first",
  DOMAIN_CHANGED: "Domain Name has changed. Please check availability again.",
  DOMAIN_ENTER_FIRST: "Please enter a Domain Name first",
  DOMAIN_AVAILABLE: "Domain is available! You cannot change it later.",
  DOMAIN_TAKEN: "This domain is already taken. Please try another one.",
  DOMAIN_CHECK_ERROR:
    "Error checking domain availability. Please try again.",
  SUBMIT_BTN: "\ud83d\udd17 Confirm & Submit",
  SUBMITTING_BTN: "Setting up...",
  SUBMIT_SUCCESS: "Hub setup completed successfully!",
  SUBMIT_ERROR: "Failed to setup hub. Please try again.",
};

/**
 * User-visible strings for Biz creation / edit screens.
 */
export const BIZ = {
  PAGE_TITLE: "Create Biz",
  STEP1_LABEL: "Step 1 of 3 \u2014 Biz Metadata",
  STEP1_DESC: "Enter your business information below.",
  DETAILS_HEADER: "\u25BE Biz Details",
  DETAILS_CARD_TITLE: "Business Details",
  DETAILS_CARD_SUBTITLE: "Tell us about your business",
  NAME_LABEL: "Business Name",
  NAME_SUBLABEL: "This is how customers will see your store",
  NAME_PLACEHOLDER: "e.g. John's Bakery",
  NAME_REQUIRED: "Please enter the Biz name.",
  TYPE_LABEL: "Business Type",
  TYPE_SUBLABEL: "Choose what you offer",
  TYPE_REQUIRED: "Please select a Biz type.",
  TYPE_PRODUCT: "Product",
  TYPE_SERVICE: "Service",
  CATEGORY_SUBLABEL: "Helps customers discover your store",
  PRODUCT_CATEGORY_PLACEHOLDER: "Select a product category",
  SERVICE_CATEGORY_PLACEHOLDER: "Select a service category",
  CONTACT_CARD_TITLE: "Contact Details",
  CONTACT_CARD_SUBTITLE: "Customers will contact you on this number",
  CONTACT_LABEL: "Phone Number",
  CONTACT_SUBLABEL: "We\u2019ll use this to verify your account & WhatsApp",
  CONTACT_HINT: "Change the number, tap Verify, then enter the WhatsApp code before saving.",
  VERIFIED_LABEL: "Verified",
  ADDRESS_LABEL: "Address",
  ADDRESS_SUBLABEL: "Enter your store address",
  ADDRESS_REQUIRED: "Please enter address!",
  LOCALITY_LABEL: "Area / Locality",
  LOCALITY_PLACEHOLDER: "e.g. GangaNagar",
  LOCALITY_REQUIRED: "Please enter area/locality!",
  CITY_LABEL: "City",
  CITY_REQUIRED: "Please enter city!",
  COUNTRY_LABEL: "Country",
  COUNTRY_REQUIRED: "Please select country!",
  ZIP_LABEL: "Zip/Postal Code",
  ZIP_REQUIRED: "Please enter zip/postal code!",
  ZIP_PATTERN: "Enter 5 or 6 digit zip/postal code",
  LOCATION_CARD_TITLE: "Business Location",
  LOCATION_CARD_SUBTITLE: "Used for customer discovery and maps",
  MAP_LOCATION_LABEL: "Geo Location",
  MAP_LOCATION_SUBLABEL: "Drop a pin on the map to set your store location",
  MAP_LOCATION_PLACEHOLDER: "https://maps.google.com/...",
  MAP_LOCATION_HINT: "Paste a Google Maps share link or use the button to open Maps and copy the URL",
  MAP_OPEN_BUTTON: "Open in Google Maps",
  STEP2_LABEL: "Step 2 of 3 \u2014 Biz Settings",
  STEP2_DESC: "Configure your business operations.",
  SETTINGS_HEADER: "\u25BE Biz settings",
  VISIBILITY_LABEL: "Visibility",
  VISIBILITY_PUBLIC: "\ud83c\udf10 Public",
  VISIBILITY_PRIVATE: "\ud83d\udd12 Private",
  OPEN_STATUS_LABEL: "Open status",
  OPEN: "Open",
  CLOSED: "Closed",
  LISTING_LABEL: "Customer listing",
  LIVE: "Live",
  DRAFT: "Draft",
  IMAGE_KEY_LABEL: "Biz display image key",
  IMAGE_KEY_PLACEHOLDER: "Media key from Media Gallery (path)",
  MEDIA_GALLERY: "Media Gallery",
  PASSKEY_LABEL: "Passkey",
  PASSKEY_PLACEHOLDER: "At least 5 characters or more",
  CONFIRM_PASSKEY_LABEL: "Confirm Passkey",
  CONFIRM_PASSKEY_PLACEHOLDER: "Confirm your passkey",
  FLASH_LABEL: "Flash Message",
  FLASH_YES: "Yes",
  FLASH_NO: "No",
  FLASH_TEXT_LABEL: "Flash Message Text",
  FLASH_TEXT_PLACEHOLDER: "Type the flash message customers will see...",
  FLASH_TEXT_REQUIRED: "Flash message cannot be empty",
  DISCOUNT_LABEL: "Special Discount (%)",
  DISCOUNT_PLACEHOLDER: "e.g. 10",
  DELIVERY_LABEL: "Delivery options",
  DELIVERY_OPTION: "\ud83d\ude9a Delivery",
  PICKUP_OPTION: "\ud83c\udfea Self Pickup",
  PAYMENT_LABEL: "Payment method",
  PAYMENT_GATEWAY: "\ud83d\udcb3 Gateway",
  PAYMENT_SELF: "\ud83e\udd1d Self-managed",
  KYC_BADGE: "KYC",
  KYC_NOTE: "\u26a0\ufe0f Gateway payments require",
  KYC_LINK: "KYC verification",
  KYC_START: "Start KYC \u2192",
  CATALOG_STEP3_LABEL: "Step 3 of 3 \u2014 Item Catalog",
  CATALOG_DESC: "Add products or services to your catalog.",
  CATALOG_HEADLINE: "Start building your storefront",
  CATALOG_BODY: "Add your first product or service to begin selling. Your catalog is the heart of your business \u2014 make it shine.",
  CATALOG_ADD_PRODUCT: "Add Product",
  CATALOG_ADD_SERVICE: "Add Service",
  CATALOG_IMPORT: "Import from Excel",
  CATALOG_IMPORT_DESC: "Upload an Excel file (.xlsx) with catalog items. Start by downloading the sample template.",
  CATALOG_IMPORT_SAMPLE: "Download sample template",
  CATALOG_IMPORT_DOWNLOAD_AGAIN: "Download sample",
  CATALOG_IMPORT_VALID: "valid",
  CATALOG_IMPORT_ERRORS: "with errors",
  CATALOG_IMPORT_TOTAL: "total rows",
  CATALOG_IMPORT_FIX: "Fix these errors in your file and re-upload",
  CATALOG_IMPORT_ITEMS_TO_IMPORT: "Items to import",
  CATALOG_IMPORT_BTN: "Import",
  CATALOG_IMPORT_SUCCESS: "item(s) imported successfully",
  CATALOG_SKIP: "Skip for now \u2192",
  CATALOG_CONTINUE: "Continue",
  BACK: "\u2190 Back",
  FOOTER_SAVE: "Save",
  SAVE_CONTINUE: "Save & Continue \u2192",
  FINISH_SETUP: "Finish setup",
  STEP_INDICATOR: "Step 1: Biz Metadata \u2192 Step 2: Biz Settings \u2192 Step 3: Item Catalog",
  PREVIEW_TITLE: "Biz Preview",
  PREVIEW_CLOSE: "Close Preview",
  CANCEL: "\u2715 Cancel",
  PREVIEW: "\ud83d\udc41 Preview",
  CREATE: "\uff0b Create Biz",
  CREATING: "Creating Biz...",
  UPDATE: "Update Biz",
  UPDATING: "Updating Biz...",

  // Progress bar step names
  STEP1_NAME: "Biz Metadata",
  STEP2_NAME: "Biz Settings",
  STEP3_NAME: "Catalog",

  // Step 2 card titles + subtitles
  OPERATIONS_CARD_TITLE: "Business Operations",
  OPERATIONS_CARD_SUBTITLE: "Configure how your business operates.",
  EXPERIENCE_CARD_TITLE: "Customer experience",
  EXPERIENCE_CARD_SUBTITLE: "Manage how customers interact with your store",
  STOREFRONT_CARD_TITLE: "Storefront settings",
  STOREFRONT_CARD_SUBTITLE: "Customize how your store appears",

  // Setting row sublabels \u2014 Card 1
  VISIBILITY_SUBLABEL: "Who can see your store on ZiniHub",
  OPEN_STATUS_SUBLABEL: "Set whether your store is open or closed",
  LISTING_SUBLABEL: "Control listing in ZiniHub customer app",

  // Setting row sublabels \u2014 Card 2
  FLASH_SUBLABEL: "Show announcement on your store",
  DISCOUNT_SUBLABEL: "Offer a discount to attract customers",
  DELIVERY_SUBLABEL: "Select all that apply",
  PAYMENT_SUBLABEL: "How customers can pay",

  // Card 2 section divider labels
  SECTION_FULFILLMENT: "Fulfillment",
  SECTION_PAYMENTS: "Payments",
  SECTION_PROMOTION: "Promotion",

  // Card 3 field labels + sublabels
  DISPLAY_IMAGE_LABEL: "Display image",
  DISPLAY_IMAGE_SUBLABEL: "Upload a key image for your store",
  DISPLAY_IMAGE_SAVE_FIRST: "Save & continue once to unlock display image upload for this store.",
  SOCIAL_FOOTPRINTS_LABEL: "Social Footprints",
  SOCIAL_FOOTPRINTS_SUBLABEL: "Optional links shown on your storefront",
  SOCIAL_YOUTUBE: "YouTube Channel",
  SOCIAL_INSTAGRAM: "Instagram",
  SOCIAL_FACEBOOK: "Facebook",
  SOCIAL_LINKEDIN: "LinkedIn",
  SOCIAL_X: "X",
  SOCIAL_URL_INVALID: "Enter a full URL starting with http:// or https://",
  BUSINESS_DOCS_SUBLABEL: "Map important documents to your store",

  // Delivery / payment hints
  DELIVERY_HINT: "Select all that apply \u2014 at least one required.",
  PAYMENT_HINT: "Gateway payments require KYC verification.",
  PAYMENT_GATEWAY_KYC_HINT: "Gateway payments require KYC verification.",
    STATE_LABEL: "State",
    STATE_REQUIRED: "State is required",

  // Section dividers — Card 1
  SECTION_BUSINESS_STATUS: "Business Status",
  SECTION_COVERAGE_AREA: "Coverage Area",
  SERVICE_COVERAGE_HELPTEXT: "Mention the neighbourhood where customers can place orders or book your services.",
  SECTION_PREBOOKING: "Pre-Order / Pre-Booking",
  SECTION_ADVANCE_BOOKING: "Advance Booking",

  // Service Coverage
  SERVICE_COVERAGE_SCOPE_ANYWHERE: "Anywhere",
  SERVICE_COVERAGE_SCOPE_CITY: "Entire City",
  SERVICE_COVERAGE_SCOPE_SELECTED_AREAS: "Selected Areas",
  SERVICE_AREAS_PLACEHOLDER: "Type a locality or area name and press Enter",
  SERVICE_AREAS_SUBTEXT: "Add the neighbourhoods or localities you serve. Max 10 areas.",

  // Pre-booking
  PREBOOKING_GROUP_LABEL: "Availability",
  PREBOOKING_DATES_LABEL: "Select Dates",
  PREBOOKING_SLOTS_LABEL: "Available Time Slots",
  PREBOOKING_SLOT_START: "Start",
  PREBOOKING_SLOT_END: "End",
  PREBOOKING_ADD_SLOT: "+ Add Slot",
  PREBOOKING_ADD_GROUP: "Add Another Date Group",
  PREBOOKING_CALENDAR_TITLE: "Availability Calendar",
  PREBOOKING_NO_SLOTS: "Anytime",
  PREBOOKING_REMOVE_GROUP: "Remove",
  PREBOOKING_MAX_ORDERS: "Max Orders Per Slot",

  // Advance Booking
  ADVANCE_BOOKING_HINT: "Customers can book",
  ADVANCE_SAME_DAY: "Same Day",
  ADVANCE_NEXT_DAY: "Next Day",
  ADVANCE_TWO_DAYS: "2 Days",
  ADVANCE_THREE_DAYS: "3 Days",
  ADVANCE_ONE_WEEK: "1 Week",
};

export const KYC_UPLOAD = {
  BACK_BTN: "\u2190 KYC",
  PAGE_TITLE: "Upload Documents",
  STEPS: ["Identity", "Business", "Bank Details"],
  STEP1_TITLE: "Government ID",
  STEP1_DESC: "Upload a clear photo or scan of your government ID",
  STEP1_FRONT: "Front side (photo + details)",
  STEP1_BACK: "Back side (address / barcode)",
  STEP2_TITLE: "Business Proof",
  STEP2_DESC: "GST certificate or shop/trade registration document",
  STEP2_FILE1: "GST Certificate",
  STEP2_FILE2: "Shop Registration",
  STEP2_NOTE: "optional",
  STEP3_TITLE: "Bank Details",
  STEP3_DESC: "Enter your bank account information",
  STEP3_ACCOUNT_LABEL: "Account Number",
  STEP3_ACCOUNT_PLACEHOLDER: "Enter account number",
  STEP3_IFSC_LABEL: "IFSC Code",
  STEP3_IFSC_PLACEHOLDER: "e.g. SBIN0001234",
  STEP3_HOLDER_LABEL: "Account Holder Name",
  STEP3_HOLDER_PLACEHOLDER: "Enter name as on bank account",
  STEP3_CHEQUE_LABEL: "Cancelled Cheque",
  STEP3_CHEQUE_DESC: "Upload a clear photo of a cancelled cheque",
  UPLOAD_BTN: "\u2191 Upload file",
  BACK: "\u2190 Back",
  CONTINUE: "Continue \u2192",
  SUBMIT: "Submit for Review",
  SKIP: "Skip this step",
  REQUIRED: "Required",
  OPTIONAL: "optional",
};

export const KYC_REQUESTED = {
  PAGE_TITLE: "KYC Verification",
  STATUS_TITLE: "Under Review",
  STATUS_ICON: "\u23f3",
  SUBMITTED_ON: "Submitted on 19 May 2026",
  BADGE: "Requested",
  INFO_TEXT: "Our team reviews KYC submissions within 2\u20133 business days. You'll receive a WhatsApp notification when the status changes.",
  FOOTNOTE: "Cannot resubmit while under review. Contact support if urgent.",
  DOCS_TITLE: "Submitted Documents",
  DOCS: [
    { icon: "\ud83e\uddea", title: "Aadhaar Card", sub: "Identity \u00b7 Front + Back" },
    { icon: "\ud83c\udfe6", title: "Bank Details", sub: "Account + Cancelled Cheque" },
  ],
  UPLOADED_LABEL: "\u2713 Uploaded",
  WHATSAPP_BANNER: "WhatsApp notification will be sent to +91-9740... when status changes",
};

export const KYC_VERIFIED = {
  PAGE_TITLE: "KYC Verification",
  STATUS_ICON: "\u2705",
  STATUS_TITLE: "KYC Verified",
  VERIFIED_ON: "Verified on 22 May 2026",
  BADGE: "Verified",
  CHIPS: [
    "\ud83d\udcb3 Gateway payments unlocked",
    "\ud83c\udfc5 Trust badge active",
    "All stores eligible",
  ],
  REVERIFY_TEXT: "If your business details change, you may need to re-verify.",
  REVERIFY_LINK: "Re-verify \u2192",
  HINT_TITLE: "\ud83d\udcb3 Gateway payment is now available",
  HINT_BODY: "Go to any Biz Settings \u2192 Payment method \u2192 select \ud83d\udcb3 Gateway to enable online payments for that store.",
  DOCS_TITLE: "Verified Documents",
  DOCS: [
    { icon: "\ud83e\uddea", title: "Aadhaar Card" },
    { icon: "\ud83c\udfe6", title: "Bank Details" },
    { icon: "\ud83c\udfe2", title: "GST Certificate" },
  ],
  VERIFIED_LABEL: "\u2705 Verified",
};

export const PRODUCT_BUILDER = {
  PAGE_TITLE: "Product Builder",
  PANEL_TITLE: "Create Product",
  SECTIONS: [
    { title: "▸ Product Identity", fields: [
      { placeholder: "Product name *", h: 28 },
      { placeholder: "Description…", h: 50 },
      { placeholder: "Segment", hint: "▾" },
    ]},
  ],
  TOGGLES: [
    { icon: "◎", label: "Variants", active: true },
    { icon: "⊕", label: "Add-ons", active: true },
    { icon: "✦", label: "Customisations", active: false },
  ],
  SAVE_DRAFT: "💾 Save Draft",
  SAVE_CLOSE: "Save & Close",
};

export const KYC_REJECTED = {
  PAGE_TITLE: "KYC Verification",
  STATUS_ICON: "\u274c",
  STATUS_TITLE: "Verification Failed",
  REVIEWED_ON: "Reviewed on 22 May 2026",
  BADGE: "Rejected",
  INFO_TEXT: "Your KYC submission could not be verified. Please fix the issues below and resubmit.",
  ISSUES_TITLE: "Issues to fix",
  ISSUES: [
    { icon: "\ud83e\uddea", title: "Aadhaar Card", reason: "Document image is blurry \u2014 please re-upload a clear scan", flagged: true },
    { icon: "\ud83c\udfe6", title: "Bank Details", reason: "Name on account does not match registered name", flagged: true },
    { icon: "\ud83c\udfe2", title: "GST Certificate", reason: "Accepted \u2713", flagged: false },
  ],
  REUPLOAD_BTN: "Re-upload Documents \u2192",
  NEED_HELP: "Need help?",
  CONTACT_SUPPORT: "Contact support \u2192",
};

export const KYC_PENDING = {
  BACK_BTN: "\u2190 Hub Settings",
  PAGE_TITLE: "KYC Verification",
  UNLOCK_TITLE: "What you unlock",
  UNLOCK_ITEMS: [
    { icon: "\ud83d\udcb3", title: "Gateway Payments", desc: "Accept online payments via Razorpay / Stripe" },
    { icon: "\ud83c\udfc5", title: "Trust Badge", desc: "Verified merchant badge on your storefront" },
    { icon: "\ud83d\udcc8", title: "More Conversions", desc: "Customers trust verified sellers more" },
  ],
  NEED_TITLE: "What you need",
  NEED_ITEMS: [
    { icon: "\ud83e\uddea", title: "Government ID", desc: "Aadhaar, PAN, or Passport (front + back)", required: true },
    { icon: "\ud83c\udfe2", title: "Business Proof", desc: "GST certificate or shop registration", required: false },
    { icon: "\ud83c\udfe6", title: "Bank Details", desc: "Account number, IFSC, cancelled cheque", required: true },
  ],
  START_BTN: "Start KYC \u2192",
};

export const HUB_SETTINGS = {
  PAGE_TITLE: "Hub Settings",
  PAGE_SUBTITLE: "Manage your hub name, address, and preferences.",
  HUB_SELECT_PLACEHOLDER: "Select a hub",
  KYC_TITLE: "KYC Verification",
  KYC_NOT_VERIFIED_BADGE: "Not Verified",
  KYC_DESCRIPTION: "Complete KYC to unlock %s for your stores. Customers can pay online directly.",
  KYC_BENEFIT_GATEWAY: "Gateway payments",
  KYC_BENEFIT_TRUST: "Trust badge on storefront",
  KYC_BENEFIT_CONFIDENCE: "Higher customer confidence",
  KYC_START_BTN: "Start KYC Verification \u2192",
  GENERAL_HEADER: "General Settings",
  HUB_NAME_LABEL: "Hub Display Name",
  HUB_NAME_REQUIRED: "Please enter hub name",
  ADDRESS_LABEL: "Address",
  ADDRESS_REQUIRED: "Please enter address",
  TIMEZONE_LABEL: "Timezone",
  SAVE_BTN: "SAVE",
  SAVING_BTN: "Saving...",
  SAVE_SUCCESS: "Hub settings saved successfully!",
  SAVE_ERROR: "Failed to save hub settings. Try again.",
  NO_HUB: "No hub found. Please set up your hub first.",
  DOMAIN_LABEL: "Domain",
  DOMAIN_HINT: "Read-only after creation",
};

/** Hub listing page (/hub) — stores grid/list. */
export const HUB_STORES = {
  CREATE_BUSINESS: "Create Business",
  CREATE_FIRST_BUSINESS: "Create Your First Business",
};

/**
 * Barrel for app-wide config. Add ORDERS_CONFIG, STORES_CONFIG, etc. here later.
 */
export const ZINIHUB = {
  SUBSCRIPTION: SUBSCRIPTION_CONFIG,
  ITEM_PRICING: ITEM_PRICING_CONFIG,
  AUTH,
  HUB_SETUP,
  BIZ,
  HUB_STORES,
  HUB_SETTINGS,
  KYC_PENDING,
  KYC_UPLOAD,
  KYC_REQUESTED,
  KYC_VERIFIED,
  KYC_REJECTED,
  PRODUCT_BUILDER,
};
